import React from 'react';
import { clsx } from 'clsx';
import styles from './SliderRuler.module.scss';

export interface SliderRulerProps {
  /**
   * Minimum value of the ruler
   */
  min: number;

  /**
   * Maximum value of the ruler
   */
  max: number;

  /**
   * Step value for minor ticks
   */
  step?: number;

  /**
   * Major tick values (where labels will be shown)
   */
  majorTicks?: number[];

  /**
   * Custom formatter for label display
   */
  formatLabel?: (value: number) => string;

  /**
   * Additional CSS class name
   */
  className?: string;
}

export const SliderRuler: React.FC<SliderRulerProps> = ({
  min,
  max,
  step = 1,
  majorTicks,
  formatLabel,
  className,
}) => {
  const getPercentage = (value: number) => {
    return ((value - min) / (max - min)) * 100;
  };

  const formatDisplayValue = (value: number) => {
    return formatLabel ? formatLabel(value) : value.toString();
  };

  // Generate major ticks if not provided
  const majorTickValues = majorTicks || [];

  // Generate all tick positions
  const allTicks: Array<{ value: number; isMajor: boolean }> = [];
  const tickSet = new Set<number>();

  // Add major ticks
  majorTickValues.forEach((value) => {
    if (value >= min && value <= max) {
      allTicks.push({ value, isMajor: true });
      tickSet.add(value);
    }
  });

  // Generate minor ticks between major ticks
  if (majorTickValues.length > 0) {
    // Add minor ticks before first major tick
    if (majorTickValues[0] > min) {
      for (let val = min; val < majorTickValues[0]; val += step) {
        const roundedVal = Math.round(val / step) * step;
        if (
          roundedVal >= min &&
          roundedVal < majorTickValues[0] &&
          !tickSet.has(roundedVal)
        ) {
          allTicks.push({ value: roundedVal, isMajor: false });
          tickSet.add(roundedVal);
        }
      }
    }

    // Add minor ticks between major ticks
    for (let i = 0; i < majorTickValues.length - 1; i++) {
      const start = majorTickValues[i];
      const end = majorTickValues[i + 1];

      for (let val = start + step; val < end; val += step) {
        const roundedVal = Math.round(val / step) * step;
        if (
          roundedVal > start &&
          roundedVal < end &&
          !tickSet.has(roundedVal)
        ) {
          allTicks.push({ value: roundedVal, isMajor: false });
          tickSet.add(roundedVal);
        }
      }
    }

    // Add minor ticks after last major tick
    if (
      majorTickValues.length > 0 &&
      majorTickValues[majorTickValues.length - 1] < max
    ) {
      const lastMajor = majorTickValues[majorTickValues.length - 1];
      for (let val = lastMajor + step; val <= max; val += step) {
        const roundedVal = Math.round(val / step) * step;
        if (
          roundedVal > lastMajor &&
          roundedVal <= max &&
          !tickSet.has(roundedVal)
        ) {
          allTicks.push({ value: roundedVal, isMajor: false });
          tickSet.add(roundedVal);
        }
      }
    }
  } else {
    // If no major ticks provided, generate ticks based on step
    for (let val = min; val <= max; val += step) {
      const roundedVal = Math.round(val / step) * step;
      if (roundedVal >= min && roundedVal <= max) {
        allTicks.push({ value: roundedVal, isMajor: false });
      }
    }
  }

  // Sort all ticks by value
  allTicks.sort((a, b) => a.value - b.value);

  const containerClasses = clsx(styles.container, className);

  return (
    <div className={containerClasses}>
      <div className={styles.tickMarks}>
        {allTicks.map((tick, index) => (
          <div
            key={`${tick.value}-${index}`}
            className={clsx(styles.tick, {
              [styles.tickMajor]: tick.isMajor,
              [styles.tickMinor]: !tick.isMajor,
            })}
            style={{ left: `${getPercentage(tick.value)}%` }}
          />
        ))}
      </div>
      {majorTickValues.length > 0 && (
        <div className={styles.labels}>
          {majorTickValues.map((value) => {
            if (value < min || value > max) return null;
            return (
              <div
                key={value}
                className={styles.label}
                style={{ left: `${getPercentage(value)}%` }}
              >
                {formatDisplayValue(value)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

SliderRuler.displayName = 'SliderRuler';
